import classnames from 'classnames';
import React, { useCallback, useMemo, useState } from 'react';
import { isNull } from 'lodash/fp';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { DatasetType } from '../../../datasets/store';
import { DragDrop } from '../../../shared/drag-n-drop/DragDrop';
import { DraggableItem } from '../../../shared/drag-n-drop/shared';
import styles from '../Reports.module.scss';
import { DocumentMetaReportField, DocumentReportField, getReportAgreementTypeIds, getReportFields, getReportMetaFields, getSelectedAgreementTypeId, setDocumentReportMetaFields, updateDocumentReportFields } from '../store';
import { Scrollable } from '../../../shared/scrollable/Scrollable';
import { OverflowTooltip } from '../../../shared/tooltip';
import { ConfigureColumnDetailsModal } from './ConfigureColumnDetailsModal';
import { AgreementTypeTabs } from './AgreementTypeTabs';
import { AgreementType } from '../../../admin/documents/store';

const { amber } = styles;

interface InstanceFieldColumnProps {
    id: string;
    label: string;
    customLabel: string;
    datasetType: DatasetType;
    includeDatasetTitle: boolean;
    fieldName: string;
    index: number;
    isDragging?: boolean;
    editColumnDetails: (id: string) => void;
}

const InstanceFieldColumn: React.FC<InstanceFieldColumnProps> = ({ id, label, datasetType, isDragging = false, customLabel, editColumnDetails, fieldName, includeDatasetTitle }) => {
    const columnLabel = useMemo(() => customLabel || label, [customLabel, label]);
    return (
        <div
            className={classnames(styles.columnWrapper, {
                [styles.draggingColumn]: isDragging,
                [styles.tableColumn]: datasetType === DatasetType.TABLE,
                [styles.formColumn]: datasetType === DatasetType.FORM
            })}
            onDoubleClick={() => editColumnDetails(id)}
        >
            {includeDatasetTitle && <OverflowTooltip className={styles.columnFieldName} overlayText={fieldName} />}
            <OverflowTooltip className={classnames(styles.columnLabel, { [styles.columnLabelReducedPadding]: includeDatasetTitle })} overlayText={columnLabel} />
        </div>
    );
};

interface MetaFieldColumnProps {
    id: string;
    label: string;
    index: number;
    isDragging?: boolean;
}

const MetaFieldColumn: React.FC<MetaFieldColumnProps> = ({ label, isDragging = false }) => (
    <div className={classnames(styles.columnWrapper, {
        [styles.draggingColumn]: isDragging,
        [styles.metaColumn]: true
    })}>
        <div className={styles.columnLabel}>{label}</div>
    </div>
);

export const ConfigureFields: React.FC = () => {
    const [selectedInstanceColumn, setSelectedInstanceColumn] = useState<string | null>(null);
    const dispatch = useAppDispatch();

    const reportAgreementTypeIds = useAppSelector(getReportAgreementTypeIds);
    const reportFields = useAppSelector(getReportFields);
    const reportMetaFields = useAppSelector(getReportMetaFields);
    const selectedAgreementTypeId = useAppSelector(getSelectedAgreementTypeId);

    const updateInstanceFields = useCallback((fields: DocumentReportField[]) => dispatch(updateDocumentReportFields(fields)), [dispatch]);
    const updateMetaFields = useCallback((metaFields: DocumentMetaReportField[]) => dispatch(setDocumentReportMetaFields(metaFields)), [dispatch]);

    const instanceListOptions = useMemo(() => reportFields.map(({ id, label }) => ({ id, label })), [reportFields]);
    const metaListOptions = useMemo(() => reportMetaFields.map(field => ({ id: field, label: field })), [reportMetaFields]);

    const updateInstanceList = useCallback((list: DraggableItem[]) => {
        const newFields = list.map(({ id }) => reportFields.find(field => field.id === id)!);
        updateInstanceFields(newFields);
    }, [updateInstanceFields, reportFields]);

    const updateMetaList = useCallback((list: DraggableItem[]) => {
        const newFields = list.map(({ id }) => reportMetaFields.find(metaField => metaField === id)!);
        updateMetaFields(newFields);
    }, [updateMetaFields, reportMetaFields]);

    const editColumnDetails = useCallback((id: string) => setSelectedInstanceColumn(id), []);
    const closeColumnDetails = useCallback(() => setSelectedInstanceColumn(null), []);

    const selectedColumn = useMemo(() => {
        if (isNull(selectedInstanceColumn)) {
            return null;
        }
        return reportFields.find(({ parentFieldId, id }) => `${parentFieldId}-${id}` === selectedInstanceColumn) || null;
    }, [reportFields, selectedInstanceColumn]);

    const getInstanceChildElement = useCallback((childId: string, index: number) => {
        const field = reportFields.find(({ id }) => id === childId);
        if (field) {
            const { id, label, datasetType, customLabel, includeDatasetTitle, fieldName, parentFieldId } = field;
            return (
                <InstanceFieldColumn
                    id={`${parentFieldId}-${id}`}
                    label={label}
                    index={index}
                    datasetType={datasetType}
                    customLabel={customLabel}
                    includeDatasetTitle={includeDatasetTitle}
                    fieldName={fieldName}
                    editColumnDetails={editColumnDetails}
                />
            );
        }
        return null;
    }, [reportFields, editColumnDetails]);

    const getInstanceDraggingElement = useCallback((childId: string, index: number) => {
        const field = reportFields.find(({ id }) => id === childId)!;
        const { id, label, datasetType, customLabel, includeDatasetTitle, fieldName, parentFieldId } = field;
        return (
            <InstanceFieldColumn
                id={`${parentFieldId}-${id}`}
                label={label}
                index={index}
                datasetType={datasetType}
                customLabel={customLabel}
                includeDatasetTitle={includeDatasetTitle}
                fieldName={fieldName}
                editColumnDetails={editColumnDetails}
                isDragging
            />
        );
    }, [reportFields, editColumnDetails]);

    const getMetaChildElement = useCallback((childId: string, index: number) => {
        const field = reportMetaFields.find(id => id === childId);

        if (childId === DocumentMetaReportField.NETTING_SUMMARY && selectedAgreementTypeId !== AgreementType.ISDA) {
            return null;
        }

        if (field) {
            return (
                <MetaFieldColumn id={field} label={field} index={index} />
            );
        }
        return null;
    }, [reportMetaFields, selectedAgreementTypeId]);

    const getMetaDraggingElement = useCallback((childId: string, index: number) => {
        const field = reportMetaFields.find(id => id === childId)!;
        return (
            <MetaFieldColumn id={field} label={field} index={index} isDragging />
        );
    }, [reportMetaFields]);

    return (
        <div className={styles.configureFieldsWrapper}>
            <div className={styles.fieldsHeader}>
                <div className={styles.fieldsTitle}>Configure the order you would like to display the selected columns</div>
                <div className={styles.keyWrapper}>
                    <div className={styles.metaColorIndicator} />
                    <div className={styles.keyOption} style={{ marginRight: '20px' }}>Document</div>
                    <div className={styles.tableColorIndicator} />
                    <div className={styles.keyOption} style={{ marginRight: '20px' }}>Table</div>
                    <div className={styles.formColorIndicator} />
                    <div className={styles.keyOption}>Form</div>
                </div>
            </div>
            <AgreementTypeTabs reportAgreementTypeIds={reportAgreementTypeIds} />
            <div className={styles.reportDroppableWrapper} data-testid='document-reporting-droppable-wrapper'>
                <Scrollable>
                    <div className={styles.multiDroppableWrapper}>
                        <DragDrop
                            getChildElement={getMetaChildElement}
                            list={metaListOptions}
                            listId='document-reporting-meta'
                            updateList={updateMetaList}
                            type='horizontal'
                            getDraggingElement={getMetaDraggingElement}
                            edgeColour={amber}
                            edgeWidth={3}
                            withScrollable={false}
                        />
                        <DragDrop
                            getChildElement={getInstanceChildElement}
                            list={instanceListOptions}
                            listId='document-reporting-instance'
                            updateList={updateInstanceList}
                            type='horizontal'
                            getDraggingElement={getInstanceDraggingElement}
                            edgeColour={amber}
                            edgeWidth={3}
                            withScrollable={false}
                        />
                    </div>
                </Scrollable>
            </div>
            <ConfigureColumnDetailsModal
                closeModal={closeColumnDetails}
                selectedColumn={selectedColumn}
            />
        </div>
    );
};
